/**
 * Servicio para manejar el almacenamiento en caché y soporte offline
 */

import { openDB, IDBPDatabase } from 'idb';

// Nombre y versión de la base de datos
const DB_NAME = 'domired-offline-db';
const DB_VERSION = 1;

// Nombres de los almacenes
const STORES: { [key: string]: string } = {
  POSTS: 'posts',
  USERS: 'users',
  COMMENTS: 'comments',
  USER_DATA: 'userData',
  SYNC_QUEUE: 'syncQueue'
};

// Interfaz para los elementos en la cola de sincronización
interface SyncQueueItem {
  id: string;
  operation: 'create' | 'update' | 'delete';
  collection: string;
  data: any;
  timestamp: number;
}

// Clase para manejar la base de datos IndexedDB
class CacheService {
  private db: IDBPDatabase | null = null;
  private isOnline: boolean = navigator.onLine;
  private offlineListeners: Function[] = [];

  constructor() {
    // Inicializar la base de datos
    this.initDB();

    // Escuchar eventos de conexión
    window.addEventListener('online', this.handleOnlineStatusChange);
    window.addEventListener('offline', this.handleOnlineStatusChange);
  }

  /**
   * Inicializa la base de datos IndexedDB
   */
  private async initDB(): Promise<void> {
    try {
      this.db = await openDB(DB_NAME, DB_VERSION, {
        upgrade(db) {
          // Crear almacenes si no existen
          if (!db.objectStoreNames.contains(STORES.POSTS)) {
            db.createObjectStore(STORES.POSTS, { keyPath: 'id' });
          }
          
          if (!db.objectStoreNames.contains(STORES.USERS)) {
            db.createObjectStore(STORES.USERS, { keyPath: 'id' });
          }
          
          if (!db.objectStoreNames.contains(STORES.COMMENTS)) {
            db.createObjectStore(STORES.COMMENTS, { keyPath: 'id' });
          }
          
          if (!db.objectStoreNames.contains(STORES.USER_DATA)) {
            db.createObjectStore(STORES.USER_DATA, { keyPath: 'id' });
          }
          
          if (!db.objectStoreNames.contains(STORES.SYNC_QUEUE)) {
            const syncStore = db.createObjectStore(STORES.SYNC_QUEUE, { keyPath: 'id' });
            syncStore.createIndex('timestamp', 'timestamp');
          }
        }
      });
      
      console.log('IndexedDB inicializada correctamente');
    } catch (error) {
      console.error('Error al inicializar IndexedDB:', error);
    }
  }

  /**
   * Maneja cambios en el estado de conexión
   */
  private handleOnlineStatusChange = async (): Promise<void> => {
    const wasOffline = !this.isOnline;
    this.isOnline = navigator.onLine;
    
    // Si estamos volviendo a estar en línea, intentar sincronizar
    if (this.isOnline && wasOffline) {
      console.log('Conexión restablecida. Iniciando sincronización...');
      await this.syncQueuedOperations();
      
      // Notificar a los listeners
      this.offlineListeners.forEach(listener => listener(true));
    } else if (!this.isOnline) {
      console.log('Conexión perdida. Modo offline activado.');
      // Notificar a los listeners
      this.offlineListeners.forEach(listener => listener(false));
    }
  };

  /**
   * Registra un listener para cambios en el estado de conexión
   * @param listener - Función a llamar cuando cambia el estado de conexión
   */
  public addOfflineListener(listener: (isOnline: boolean) => void): void {
    this.offlineListeners.push(listener);
  }

  /**
   * Elimina un listener para cambios en el estado de conexión
   * @param listener - Función a eliminar
   */
  public removeOfflineListener(listener: Function): void {
    this.offlineListeners = this.offlineListeners.filter(l => l !== listener);
  }
  
  /**
   * Alias para addOfflineListener para mantener compatibilidad con el nuevo código
   * @param listener - Función a llamar cuando cambia el estado de conexión
   */
  public addConnectionListener(listener: (isOnline: boolean) => void): void {
    this.addOfflineListener(listener);
  }

  /**
   * Alias para removeOfflineListener para mantener compatibilidad con el nuevo código
   * @param listener - Función a eliminar
   */
  public removeConnectionListener(listener: Function): void {
    this.removeOfflineListener(listener);
  }

  /**
   * Guarda datos en caché
   * @param storeName - Nombre del almacén
   * @param data - Datos a guardar
   */
  public async saveToCache<T>(storeName: string, data: T): Promise<void> {
    if (!this.db) {
      await this.initDB();
    }
    
    try {
      if (this.db) {
        const tx = this.db.transaction(storeName, 'readwrite');
        const store = tx.objectStore(storeName);
        await store.put(data);
        await tx.done;
      }
    } catch (error) {
      console.error(`Error al guardar en caché (${storeName}):`, error);
    }
  }

  /**
   * Guarda múltiples elementos en caché
   * @param storeName - Nombre del almacén
   * @param items - Elementos a guardar
   */
  public async saveManyToCache<T>(storeName: string, items: T[]): Promise<void> {
    if (!this.db) {
      await this.initDB();
    }
    
    try {
      if (this.db) {
        const tx = this.db.transaction(storeName, 'readwrite');
        const store = tx.objectStore(storeName);
        
        for (const item of items) {
          await store.put(item);
        }
        
        await tx.done;
      }
    } catch (error) {
      console.error(`Error al guardar múltiples elementos en caché (${storeName}):`, error);
    }
  }

  /**
   * Obtiene un elemento de la caché
   * @param storeName - Nombre del almacén
   * @param id - ID del elemento
   * @returns El elemento o null si no existe
   */
  public async getFromCache<T>(storeName: string, id: string): Promise<T | null> {
    if (!this.db) {
      await this.initDB();
    }
    
    try {
      if (this.db) {
        return await this.db.get(storeName, id);
      }
    } catch (error) {
      console.error(`Error al obtener de caché (${storeName}, ${id}):`, error);
    }
    
    return null;
  }

  /**
   * Obtiene todos los elementos de un almacén
   * @param storeName - Nombre del almacén
   * @returns Array con todos los elementos
   */
  public async getAllFromCache<T>(storeName: string): Promise<T[]> {
    if (!this.db) {
      await this.initDB();
    }
    
    try {
      if (this.db) {
        return await this.db.getAll(storeName);
      }
    } catch (error) {
      console.error(`Error al obtener todos los elementos de caché (${storeName}):`, error);
    }
    
    return [];
  }

  /**
   * Elimina un elemento de la caché
   * @param storeName - Nombre del almacén
   * @param id - ID del elemento
   */
  public async removeFromCache(storeName: string, id: string): Promise<void> {
    if (!this.db) {
      await this.initDB();
    }
    
    try {
      if (this.db) {
        await this.db.delete(storeName, id);
      }
    } catch (error) {
      console.error(`Error al eliminar de caché (${storeName}, ${id}):`, error);
    }
  }

  /**
   * Añade una operación a la cola de sincronización
   * @param operation - Tipo de operación
   * @param collection - Colección de Firestore
   * @param data - Datos de la operación
   */
  public async addToSyncQueue(
    operation: 'create' | 'update' | 'delete',
    collection: string,
    data: any
  ): Promise<void> {
    const queueItem: SyncQueueItem = {
      id: `${collection}_${data.id}_${Date.now()}`,
      operation,
      collection,
      data,
      timestamp: Date.now()
    };
    
    await this.saveToCache(STORES.SYNC_QUEUE, queueItem);
  }

  /**
   * Sincroniza las operaciones en cola con el servidor
   */
  public async syncQueuedOperations(): Promise<void> {
    if (!this.isOnline || !this.db) {
      return;
    }
    
    try {
      // Obtener todas las operaciones pendientes
      const pendingOperations = await this.getAllFromCache<SyncQueueItem>(STORES.SYNC_QUEUE);
      
      // Ordenar por timestamp
      pendingOperations.sort((a, b) => a.timestamp - b.timestamp);
      
      for (const operation of pendingOperations) {
        try {
          // Aquí implementaríamos la lógica para sincronizar con Firestore
          // Por ahora, solo eliminamos de la cola
          await this.removeFromCache(STORES.SYNC_QUEUE, operation.id);
          console.log(`Operación sincronizada: ${operation.operation} en ${operation.collection}`);
        } catch (error) {
          console.error(`Error al sincronizar operación:`, operation, error);
        }
      }
    } catch (error) {
      console.error('Error al sincronizar operaciones en cola:', error);
    }
  }

  /**
   * Comprueba si hay conexión a Internet
   * @returns true si hay conexión, false en caso contrario
   */
  public isConnected(): boolean {
    return this.isOnline;
  }

  /**
   * Limpia toda la caché
   */
  public async clearCache(): Promise<void> {
    if (!this.db) {
      await this.initDB();
    }
    
    try {
      if (this.db) {
        const stores = Object.values(STORES);
        
        for (const store of stores) {
          const tx = this.db.transaction(store, 'readwrite');
          const objectStore = tx.objectStore(store);
          await objectStore.clear();
          await tx.done;
        }
        
        console.log('Caché limpiada correctamente');
      }
    } catch (error) {
      console.error('Error al limpiar la caché:', error);
    }
  }
}

// Exportar una instancia única del servicio
const cacheService = new CacheService();
export default cacheService;

// Exportar nombres de almacenes para uso en la aplicación
export { STORES };
